import React, { useEffect, useState, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { clearUser } from '../store/authSlice'
import authService from '../services/authService'
import restaurantStaffService from '../services/restaurantStaffService'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { useTranslation } from '../hooks/use-i18n'
import { useAuthNavLinks } from '../hooks/useAuthNavLinks'
import LanguageSelector from './LanguageSelector'

const linkClass = (active: boolean) =>
  `text-sm font-medium transition-colors whitespace-nowrap ${
    active ? 'text-blue-500 underline' : 'text-white hover:text-white/80'
  }`

const mobileLinkClass = (active: boolean) =>
  `text-sm font-medium transition-colors px-3 py-2 rounded-lg ${
    active ? 'text-sky-400 bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/10'
  }`

const Navigation: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [pendingInvites, setPendingInvites] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)
  const authNavLinks = useAuthNavLinks(location.pathname)

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingInvites(0)
      return
    }
    let active = true
    restaurantStaffService
      .getMyInvitations()
      .then((invites) => {
        if (active) {
          setPendingInvites(invites.filter((i) => i.status === 'pending').length)
        }
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [isAuthenticated, location.pathname])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobileMenuOpen])

  const handleLogout = () => {
    authService.logout()
    dispatch(clearUser())
    navigate('/')
    setIsMobileMenuOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  if (location.pathname === '/') {
    return null
  }

  return (
    <nav ref={menuRef} className="bg-[#111111] border-b border-white/10 fixed top-0 left-0 right-0 z-50 w-full">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 w-full">
        <div className="flex justify-between items-center py-3 sm:py-4 min-h-[56px] sm:min-h-[64px] gap-3">
          <Link
            to={isAuthenticated ? '/dashboard' : '/'}
            className="hidden min-[320px]:flex items-center transition-all duration-300 shrink-0"
          >
            <img src="/assets/restoside_only_logo.png" alt={t('common.logoAlt')} className="h-8 w-auto lg:hidden" />
            <img src="/assets/restoside_with_logo.png" alt={t('common.logoAlt')} className="h-9 w-auto hidden lg:block" />
          </Link>

          <div className="hidden md:flex items-center gap-3 lg:gap-5 flex-1 justify-end min-w-0 overflow-x-auto">
            {isAuthenticated ? (
              <>
                {authNavLinks.map((link) => (
                  <Link key={link.to} to={link.to} className={`relative ${linkClass(link.active)}`}>
                    {link.label}
                    {link.to === '/account' && pendingInvites > 0 && (
                      <span className="absolute -top-2 -right-3 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                        {pendingInvites}
                      </span>
                    )}
                  </Link>
                ))}
                <span className="text-white text-sm hidden lg:inline whitespace-nowrap">
                  {t('nav.welcome', { name: user?.name || '' })}
                </span>
                <LanguageSelector />
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-white/80 text-sm font-medium transition-all duration-300 whitespace-nowrap"
                >
                  {t('nav.signOut')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap"
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="text-white hover:text-white/80 text-sm font-medium whitespace-nowrap"
                >
                  {t('nav.signUp')}
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center ml-auto">
            <button
              onClick={toggleMobileMenu}
              className="text-white/80 hover:text-white p-2 rounded-lg transition-all duration-300 hover:bg-white/10"
              aria-label={t('common.toggleMobileMenu')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6L18 18" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 py-4 animate-fade-in">
            <div className="flex flex-col space-y-3">
              {isAuthenticated ? (
                <>
                  {authNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center justify-between ${mobileLinkClass(link.active)}`}
                    >
                      <span>{link.label}</span>
                      {link.to === '/account' && pendingInvites > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                          {pendingInvites}
                        </span>
                      )}
                    </Link>
                  ))}
                  <div className="px-3 py-2 border-t border-white/10">
                    <div className="text-white/80 text-sm mb-2">{t('nav.welcome', { name: user?.name || '' })}</div>
                    <div className="mb-3">
                      <p className="text-xs font-medium text-white/50 uppercase tracking-wide mb-2">{t('common.language')}</p>
                      <LanguageSelector variant="inline" />
                    </div>
                    <button
                      onClick={handleLogout}
                      className="text-white/80 hover:text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-white/10 w-full text-left"
                    >
                      {t('nav.signOut')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 text-center"
                  >
                    {t('nav.signIn')}
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-white/80 hover:text-white px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    {t('nav.signUp')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default Navigation
